import { cloneDeep, isEqual } from 'lodash';
import { TimeRecordHeader, TimeRecordDetail, TimeRecordHours, getTimesheetForWeekEnding, postTimesheet, putTimesheet } from './TimesheetAPI';
import { ShopJobParts, ShopJobHeader } from '../shop/ShopJobsAPI';
import { CancelToken } from 'axios';
import { addDays, getUTCDateString } from '../../utils/Functions';
import { getCostCodesforJob, EarningsType, CYMAJob } from '../../utils/CYMAAPI';
import { getWeekEndingDate, parseStringNumber, sortEarningsTypes } from '../../utils/TSFunctions';
import { Employee } from '../../utils/AccountsAPI';
let dateFormat = require('dateformat'); //formats dates very well

/*
    Name: Timesheet
    Author: Tyler Kennedy
    Date: 9/8/2020
    Purpose: Timesheet Middleware
*/

const days: { day: string; abb: string; hours?: number }[] = [
  { day: 'Sunday', abb: 'Sun', hours: undefined },
  { day: 'Monday', abb: 'Mon', hours: undefined },
  { day: 'Tuesday', abb: 'Tue', hours: undefined },
  { day: 'Wednesday', abb: 'Wed', hours: undefined },
  { day: 'Thursday', abb: 'Thur', hours: undefined },
  { day: 'Friday', abb: 'Fri', hours: undefined },
  { day: 'Saturday', abb: 'Sat', hours: undefined },
];

/*
------------------------------------------------------
-------------------Timesheet Class--------------------
------------------------------------------------------
*/

export default class Timesheet {
  _header: TimeRecordHeader;
  _dateCreated: Date | null;
  _weekEnding: Date;
  _employeeInfo: Employee;
  _jobs: CYMAJob[] = [];
  schedulerJobs: CYMAJob[] = [];
  earningsTypes?: EarningsType[];

  constructor(employeeInfo: Employee, weekEnding: Date, jobs: CYMAJob[] = [], earningsTypes: EarningsType[] = [], schedulerJobs: CYMAJob[] = []) {
    this._header = this.emptyTimesheet();
    this._header.periodEndingDate = weekEnding;
    this._weekEnding = weekEnding;
    this.weekEnding = weekEnding;
    this._dateCreated = new Date();
    this._employeeInfo = employeeInfo;
    this.employeeInfo = employeeInfo;

    this.jobs = jobs;

    this.schedulerJobs = schedulerJobs;

    this.earningsTypes = sortEarningsTypes(earningsTypes);
  }

  set jobs(j) {
    this._jobs = j;
  }

  get jobs() {
    var jobsClone = cloneDeep(this._jobs);
    this.earningsTypes?.filter((et) => et.canReplaceJob).forEach((et) => jobsClone.push({ jobNumber: et.shortName, desc: et.shortName }));
    return jobsClone;
  }

  set weekEnding(weekEnding: Date) {
    if (weekEnding !== this._weekEnding) {
      this._header.periodEndingDate = weekEnding;
      this._weekEnding = getWeekEndingDate(weekEnding);
    }
  }

  get weekEnding(): Date {
    return this._weekEnding;
  }

  set employeeInfo(employeeInfo: Employee) {
    this._header.userId = employeeInfo.id;
    this._employeeInfo = employeeInfo;
  }

  get employeeInfo(): Employee {
    return this._employeeInfo;
  }

  set data(timesheet) {
    timesheet.userId = timesheet.userId ?? this._employeeInfo.id;
    timesheet.periodEndingDate = timesheet.periodEndingDate ?? this.getWeekEndingAsString();
    this._header = timesheet;
  }

  get data() {
    return this._header;
  }

  get locked() {
    return this._header.managerApprovedBy || this._header.accountingApprovedBy || this.isExpired ? true : false;
  }

  get isExpired() {
    return (new Date().getTime() - this._weekEnding?.getTime()) / (1000 * 60 * 60 * 24) > 21;
  }

  get totalHours() {
    return this._header.details.reduce((sum, d) => sum + d.hours.reduce((s, h) => s + parseStringNumber(h.hours), 0), 0);
  }

  get hoursByEarningsType() {
    return this._header.details.reduce(
      (agg, det) => {
        if (!det.earningsType) return agg;

        if ((det.earningsType?.indexOf('Reg') ?? -1) > -1 || (det.earningsType?.indexOf('Rg') ?? -1) > -1) {
          agg.reg += det.hours.reduce((s, h) => s + parseStringNumber(h.hours), 0);
        } else if ((det.earningsType?.indexOf('OT') ?? -1) > -1) {
          agg.ot += det.hours.reduce((s, h) => s + parseStringNumber(h.hours), 0);
        }

        return agg;
      },
      { reg: 0, ot: 0 } as Record<'reg' | 'ot', number>
    );
  }

  get otHoursWarning() {
    const totalHours = this.hoursByEarningsType;
    return ((totalHours.reg ?? 0) < 40 && (totalHours.ot ?? 0) > 0) || (totalHours.reg ?? 0) > 40;
  }

  get isManagerApproved() {
    return this._header.managerApprovedBy ? true : false;
  }

  get isAccountingApproved() {
    return this._header.accountingApprovedBy ? true : false;
  }

  get objType() {
    return 'timesheet';
  }

  get errors() {
    return this.getErrors();
  }

  getErrors(validateJob = true, validateCostCode = true): string[] {
    let errorMessages: string[] = [];
    let totalHours = 0;

    this._header.details.forEach((detail, i) => {
      if (isEqual(detail, this.emptyDetail())) {
        return;
      }

      //First Checking TimeTicketDetail
      if (validateJob) {
        let jobIndex = this.jobs?.findIndex((job) => {
          return job.jobNumber === detail.jobNumber;
        });

        jobIndex = Math.max(
          jobIndex ?? -1,
          this.schedulerJobs.findIndex((job) => {
            return job.jobNumber === detail.jobNumber;
          })
        );

        if (jobIndex === -1 && (this.earningsTypes?.findIndex((et) => et.shortName === detail.earningsType && et.canReplaceJob) ?? -1) === -1) {
          errorMessages.push('Invalid Job #: [' + detail.jobNumber + '], on row:' + (i + 1));
        }
      }

      if (
        validateCostCode &&
        (detail.costCode === '' || !detail.costCode) &&
        (this.earningsTypes?.findIndex((et) => et.shortName === detail.earningsType && et.canReplaceJob) ?? -1) === -1
      ) {
        errorMessages.push('Invalid Cost Code on row:' + (i + 1));
      }

      const earningsTypeIndex = this.earningsTypes?.findIndex((earningsType) => {
        return earningsType.shortName === detail.earningsType;
      });

      if (this.earningsTypes?.findIndex((et) => et.shortName === detail.earningsType && et.canReplaceJob) && detail.shopJobParts)
        errorMessages.push(`${detail.earningsType} cannot have a part assigned!`);

      if (earningsTypeIndex === -1) errorMessages.push('Invalid Earnings Type #: [' + detail.earningsType + '], on row:' + (i + 1));

      if (this?.employeeInfo?.skills && this?.employeeInfo?.skills?.length !== 0 && !detail.skill) errorMessages.push('No skill assigned on row:' + (i + 1));

      detail.hours.forEach((hour) => {
        if (hour.hours) {
          if (hour.hours > 24) {
            errorMessages.push('No more than 24 hours per day: ' + hour.hours);
          } else if (hour.hours < 0) {
            errorMessages.push('No negative hours: ' + hour.hours);
          } else if (isNaN(parseFloat(hour.hours.toString()))) {
            errorMessages.push('Hours must be a number: ' + hour.hours);
          }
          totalHours += parseFloat(hour.hours.toString());
        } else {
          errorMessages.push('Empty hour record!');
        }
      });

      if (totalHours === 0) {
        errorMessages.push('Total hours must be greater than zero');
      }
      totalHours = 0;
    });

    return errorMessages;
  }

  copy(): Timesheet {
    return cloneDeep(this);
  }

  updateHeader(fieldId: string, value: any): Timesheet {
    let clone = cloneDeep(this);
    if (clone._header[fieldId] !== value) {
      clone._header[fieldId] = value;
    }
    return clone;
  }

  newDetail(): Timesheet {
    let clone = cloneDeep(this);
    clone._header.details.push(clone.emptyDetail());
    return clone;
  }

  withEmptyDetail(): Timesheet {
    var clone = cloneDeep(this);
    var existingIndex = clone._header.details.findIndex((detail) => !detail.id);
    if (existingIndex > -1) return clone;
    else {
      clone._header.details.push(clone.emptyDetail());
      return clone;
    }
  }

  removeDetail(rowNumber: number): Timesheet {
    let clone = cloneDeep(this);
    let header = clone._header;
    header.details.splice(rowNumber, 1);
    if (header.details.length === 0) {
      header.details = [clone.emptyDetail()];
    }
    return clone;
  }

  updateDetailToNewJob(rowNum: number, token?: CancelToken): Promise<Timesheet> {
    let clone = cloneDeep(this);
    let promise: Promise<any> | null = null;
    let detail = clone._header.details[rowNum];

    if (!detail.skill && clone._employeeInfo.skills.length === 1) {
      detail.skill = clone._employeeInfo.skills[0].name;
    }

    //if valid job number
    if (detail.jobNumber && detail.jobNumber !== '') {
      if (detail.jobNumber !== (detail.costCodes[0] ? detail.costCodes[0].jobNumber : '')) {
        let job = clone.jobs?.find((job) => job.jobNumber === detail.jobNumber) ?? clone.schedulerJobs?.find((job) => job.jobNumber === detail.jobNumber);
        if ((job?.costCodes?.length ?? 0) > 0) promise = Promise.resolve(job?.costCodes);
        else promise = getCostCodesforJob(detail.jobNumber, token);
      }

      if (detail.jobNumber && (this.earningsTypes?.findIndex((et) => et.shortName === detail.jobNumber && et.canReplaceJob) ?? -1) > -1) {
        detail.earningsType = detail.jobNumber;
      }
    } else {
      detail.costCodes = [];
    }

    if (promise) {
      return promise.then((response) => {
        let detail = clone._header.details[rowNum];
        detail.shopJobParts = undefined;
        detail.shopJobPartsID = undefined;
        if (detail) {
          detail.costCodes = response;
          if (detail.costCodes?.length === 1) detail.costCode = detail.costCodes[0]?.code;
        }
        return clone;
      });
    } else {
      return Promise.resolve(clone);
    }
  }

  static updateDetailToNewJob(detail: TimeRecordDetail, timesheet: Timesheet, jobs?: CYMAJob[]): TimeRecordDetail {
    let clone = cloneDeep(timesheet);

    if (!detail.skill && clone._employeeInfo.skills.length === 1) {
      detail.skill = clone._employeeInfo.skills[0].name;
    }

    //if valid job number
    if (detail.jobNumber && detail.jobNumber !== '') {
      if (detail.jobNumber !== (detail.costCodes[0] ? detail.costCodes[0].jobNumber : '')) {
        let job = (jobs ?? clone.jobs)?.find((job) => job.jobNumber === detail.jobNumber) ?? clone.schedulerJobs.find((job) => job.jobNumber === detail.jobNumber);
        detail.costCodes = job?.costCodes ?? [];
        if (detail.costCodes?.length === 1) detail.costCode = detail.costCodes[0]?.code;
        else detail.costCode = undefined;
        detail.shopJobParts = undefined;
        detail.shopJobPartsID = undefined;
      }

      if ((clone.earningsTypes?.findIndex((et) => et.shortName === detail.jobNumber && et.canReplaceJob) ?? -1) > -1) {
        detail.earningsType = detail.jobNumber;
      } else {
        detail.earningsType = '';
      }
    } else {
      detail.costCodes = [];
      detail.costCode = undefined;
    }

    return detail;
  }

  updateDetail(rowNum: number, fieldId: string, newValue: any, token?: CancelToken): Promise<Timesheet> {
    let clone = cloneDeep(this);
    const value = newValue ?? '';

    //no update
    if (clone._header?.details[rowNum]?.[fieldId] === value) {
      return Promise.resolve(this);
    }

    if (!clone._header.details[rowNum]) return Promise.reject(clone);

    clone._header.details[rowNum][fieldId] = value;

    // TODO : FIX bad earnings type for job
    if (!clone._header.details[rowNum].skill && clone._employeeInfo.skills.length === 1) {
      clone._header.details[rowNum].skill = clone._employeeInfo.skills[0].name;
    }

    if (fieldId === 'jobNumber') {
      return clone.updateDetailToNewJob(rowNum, token);
    } else if (fieldId === 'earningsType') {
      if ((this.earningsTypes?.findIndex((et) => et.shortName === value && et.canReplaceJob) ?? -1) > -1) {
        clone._header.details[rowNum].shopJobParts = undefined;
        clone._header.details[rowNum].shopJobPartsID = undefined;
      }
    }

    return Promise.resolve(clone);
  }

  updateTicketHours(rowNum: number, dayAbbr: string, newValue: any): Timesheet {
    let clone = cloneDeep(this);
    const value = isNaN(newValue) || newValue === null || newValue === undefined || newValue === '' ? '0' : newValue;

    //clear all records for that day
    clone._header.details[rowNum].hours.forEach((hourRecord, i) => {
      let day: Date | undefined;

      if (typeof hourRecord.dateOfRecord === 'string') day = new Date(Date.parse(hourRecord.dateOfRecord));
      else day = hourRecord.dateOfRecord;

      if (day && days[day.getUTCDay()].abb === dayAbbr) {
        clone._header.details[rowNum].hours.splice(i, 1);
      }
    });

    //add record back if needed
    if (!(value === '0')) {
      let newHourRecord = clone.emptyTicketHours();
      newHourRecord.detailId = clone._header.details[rowNum].id;
      newHourRecord.dateOfRecord = addDays(clone._weekEnding, days.findIndex((day) => day.abb === dayAbbr) - 6);
      newHourRecord.hours = value;
      clone._header.details[rowNum].hours.push(newHourRecord);
    }
    return clone;
  }

  static updateTicketHoursForDetail(detail: TimeRecordDetail, weekEnding: Date, dayAbbr: string, newValue: string): TimeRecordDetail {
    const value = !newValue || newValue === '' ? 0 : parseStringNumber(newValue);

    //clear all records for that day
    detail.hours.forEach((hourRecord, i) => {
      let day: Date | undefined;

      if (typeof hourRecord.dateOfRecord === 'string') day = new Date(Date.parse(hourRecord.dateOfRecord));
      else day = hourRecord.dateOfRecord;

      if (day && days[day.getUTCDay()].abb === dayAbbr) {
        detail.hours.splice(i, 1);
      }
    });

    //add record back if needed
    if (!(value === 0)) {
      let newHourRecord = Timesheet.emptyTicketHours();
      newHourRecord.detailId = detail.id;
      newHourRecord.dateOfRecord = dateFormat(addDays(weekEnding, days.findIndex((day) => day.abb === dayAbbr) - 6), 'isoUtcDateTime');
      newHourRecord.hours = value;
      detail.hours.push(newHourRecord);
    }
    return detail;
  }

  updateTicketHoursByID(rowNum: number, hourId: string, field: string, newValue: any, removeEmptyDetail: boolean = false, removeEmptyHours: boolean = true): Timesheet {
    let clone = cloneDeep(this);
    const value = (field === 'hours' && isNaN(newValue)) || newValue === null ? '0' : newValue;

    //find hour record
    let hourRecordIndex = clone._header.details[rowNum].hours.findIndex((hourRecord, i) => {
      return hourRecord.id === hourId;
    });

    if (hourRecordIndex > -1) {
      if (field === 'hours' && (value === '0' || value === 0) && removeEmptyHours) {
        clone._header.details[rowNum].hours.splice(hourRecordIndex, 1);
        if (removeEmptyDetail && clone._header.details[rowNum]?.hours?.length === 0) clone._header.details.splice(rowNum, 1);
      } else {
        clone._header.details[rowNum].hours[hourRecordIndex][field] = value;
      }
    }

    return clone;
  }

  updateTicketDetailPart(rowNum: number, field: string, newValue: any): Timesheet {
    let clone = cloneDeep(this);

    //find hour record
    let detailRecord = clone._header.details[rowNum];

    //update shopJobParts
    if (detailRecord?.shopJobParts) {
      detailRecord.shopJobParts[field] = newValue;
    }

    return clone;
  }

  changeTicketPart(rowNum: number, hourId: string, newPart: ShopJobParts, newJob: ShopJobHeader, earningsType: string): Promise<Timesheet> {
    let clone = cloneDeep(this) as Timesheet;

    //find hour record
    let hourRecord = clone._header.details[rowNum]?.hours?.find((hourRecord, i) => {
      return hourRecord.id === hourId;
    });

    //update shopJobParts
    if (hourRecord) {
      //move/update detail if necessary
      return clone.moveHourRecord(rowNum, hourId, newPart, newJob, earningsType, false);
    }

    return Promise.reject(clone);
  }

  moveHourRecord(rowNum: number, hourId: string, part: ShopJobParts, job: ShopJobHeader, earningsType: string, cloneThis = true): Promise<Timesheet> {
    let clone = cloneThis ? cloneDeep(this) : this;

    const jobNumber = job.cymaJobNumber;

    //if necessary to move
    if (
      clone._header.details[rowNum].jobNumber !== jobNumber ||
      clone._header.details[rowNum].earningsType !== earningsType ||
      clone._header.details[rowNum].shopJobPartsID !== part.id
    ) {
      //if only hour record, just swap job number & earnings type
      if (clone._header.details[rowNum].hours.length === 1) {
        let detail = clone._header.details[rowNum];
        detail.earningsType = earningsType;
        detail.shopJobPartsID = part.id;
        detail.shopJobParts = part;
        detail.jobNumber = jobNumber;

        return getCostCodesforJob(jobNumber).then((codes) => {
          detail.costCodes = codes;
          detail.costCode = codes?.[0]?.code ?? 'ALL';
          return clone;
        });
      }

      //find hour record
      let hourIndex = clone._header.details[rowNum]?.hours?.findIndex((hourRecord, i) => {
        return hourRecord.id === hourId;
      });

      //if no hour record found, quit
      if (hourIndex === -1) return Promise.reject(clone);

      //remove matching hour record (we know this exists from index earlier)
      let hourRecord = clone._header.details[rowNum]?.hours?.splice(hourIndex, 1)?.[0];

      //get numeric hours
      let hourRecordHours = (typeof hourRecord.hours === 'string' ? parseFloat(hourRecord.hours) : hourRecord.hours) ?? 0;

      //find matching detail
      let existingDetailTicket = clone._header.details.find(
        (detail) => detail.jobNumber === jobNumber && detail.earningsType === earningsType && detail.shopJobPartsID === part.id
      );

      if (existingDetailTicket) {
        //find record for same day on detail
        let existingHourRecord = existingDetailTicket.hours.find((hour) => hour.dateOfRecord === hourRecord?.dateOfRecord);

        //if exists, try to merge
        if (existingHourRecord) {
          let existingHourRecordHours = (typeof existingHourRecord.hours === 'string' ? parseFloat(existingHourRecord.hours) : existingHourRecord.hours) ?? 0;

          //if same part, add hours to existing record
          existingHourRecord.hours = existingHourRecordHours + hourRecordHours;
          return Promise.resolve(clone);
        } else {
          //else move hour record
          hourRecord.detailId = existingDetailTicket.id;
          existingDetailTicket.hours.push(hourRecord);
          return Promise.resolve(clone);
        }
      } //else fallthrough to add new detail ticket

      // add new ticket
      let detail = this.emptyDetail();
      detail.hours.push(hourRecord);

      detail.headerId = clone._header.id;
      detail.jobNumber = jobNumber;
      detail.earningsType = earningsType;
      detail.shopJobPartsID = part.id;
      detail.shopJobParts = part;

      clone._header.details.push(detail);

      return getCostCodesforJob(jobNumber).then((codes) => {
        detail.costCodes = codes;
        detail.costCode = codes?.[0]?.code ?? 'ALL';
        return clone;
      });
    }

    return Promise.resolve(clone);
  }

  updateApprovals(approvalType: 'Accounting' | 'Manager', approvedBy: string | undefined) {
    let clone = cloneDeep(this);
    if (approvalType === 'Accounting') {
      clone._header.accountingApprovedBy = approvedBy;
      clone._header.accountingApprovedDate = approvedBy ? new Date() : undefined;
    } else if (approvalType === 'Manager') {
      clone._header.managerApprovedBy = approvedBy;
      clone._header.managerApprovedDate = approvedBy ? new Date() : undefined;
    }
    return clone;
  }

  getApprovals(approvalType: 'Accounting' | 'Manager') {
    if (approvalType === 'Accounting') {
      return this._header.accountingApprovedBy;
    } else if (approvalType === 'Manager') {
      return this._header.managerApprovedBy;
    } else return false;
  }

  //formatted returns
  getWeekEndingAsString() {
    return getUTCDateString(this._weekEnding);
  }

  getHoursAsDays(rowNum: number) {
    const timeTicket = this._header.details[rowNum];
    let days = this.emptyDays();

    timeTicket?.hours?.forEach((hourRecord) => {
      let day: Date | undefined;

      if (typeof hourRecord.dateOfRecord === 'string') day = new Date(Date.parse(hourRecord.dateOfRecord));
      else day = hourRecord.dateOfRecord;

      if (day) days[day.getUTCDay()].hours = hourRecord.hours;
    });

    return days;
  }

  static getHoursForDay(detail: TimeRecordDetail, abbr: string) {
    let days = this.emptyDays();
    detail?.hours?.forEach((hourRecord) => {
      let day: Date | undefined;

      if (typeof hourRecord.dateOfRecord === 'string') day = new Date(Date.parse(hourRecord.dateOfRecord));
      else day = hourRecord.dateOfRecord;

      if (day) days[day.getUTCDay()].hours = parseStringNumber(hourRecord.hours);
    });

    return days?.find((d) => d.abb === abbr)?.hours;
  }

  static getTotalHours(detail: TimeRecordDetail) {
    return detail?.hours?.reduce((sum, hourRecord) => sum + parseStringNumber(hourRecord?.hours ?? 0), 0);
  }

  getGridTotals() {
    let totalDetail = this.emptyDetail();
    totalDetail.jobNumber = 'Totals:';
    this._header.details?.forEach((detail) => {
      detail.hours?.forEach((hourRecord) => {
        let day: Date | undefined;

        if (typeof hourRecord.dateOfRecord === 'string') day = new Date(Date.parse(hourRecord.dateOfRecord));
        else day = hourRecord.dateOfRecord;

        let newHourRecord = totalDetail.hours.find((a) => Date.parse(a.dateOfRecord?.toString() ?? '') === day?.getTime());
        if (!newHourRecord) {
          newHourRecord = this.emptyTicketHours();
          newHourRecord.dateOfRecord = day;
          totalDetail.hours.push(newHourRecord);
        }

        newHourRecord.hours = parseStringNumber(hourRecord.hours) + parseStringNumber(newHourRecord.hours);
      });
    });

    return totalDetail;
  }

  getTotalHours() {
    return this._header.details?.reduce((total, detail) => {
      return (
        total +
        detail.hours?.reduce((sum, hourRecord) => {
          return sum + parseStringNumber(hourRecord.hours);
        }, 0)
      );
    }, 0);
  }

  //api calls
  getAPITimesheet(token?: CancelToken): Promise<Timesheet> {
    if (this._employeeInfo && this._weekEnding) {
      const promise = getTimesheetForWeekEnding(this._employeeInfo.id, this.getWeekEndingAsString(), token);
      return promise
        .then((response) => {
          let clone = cloneDeep(this) as Timesheet;
          if (response.details.length === 0) response.details = [clone.emptyDetail()];
          clone.data = response;
          return clone;
        })
        .catch((err) => {
          if (err.response) {
            if (err.response.status !== 404) return Promise.reject(err);
            let clone = cloneDeep(this) as Timesheet;
            clone.data = clone.emptyTimesheet();
            return Promise.resolve(clone);
          } else return Promise.reject(err);
        });
    } else {
      return Promise.reject({
        message: 'Timesheet is missing: ' + (this._employeeInfo ? '' : 'Employee Info ') + (this._weekEnding ? '' : 'Week Ending Date'),
      });
    }
  }

  save(validateJob = true, validateCostCode = true, validate = true): Promise<Timesheet> {
    let clone = cloneDeep(this) as Timesheet;
    if (!validate || clone.getErrors(validateJob, validateCostCode).length === 0) {
      clone._header.details = clone._header.details.filter((ticket) => {
        return !isEqual(ticket, clone.emptyDetail());
      });

      if (clone._header.id) {
        return putTimesheet(clone.data).then((response) => {
          if (response.details.length === 0) response.details = [clone.emptyDetail()];
          clone.data = response;
          return clone;
        });
      } else {
        return postTimesheet(clone.data).then((response) => {
          if (response.details.length === 0) response.details = [clone.emptyDetail()];
          clone.data = response;
          return clone;
        });
      }
    } else {
      return Promise.reject('Timesheet not saved. Fix data errors before saving.');
    }
  }

  //empty data function versions
  emptyTimesheet(): TimeRecordHeader {
    return Timesheet.emptyTimesheet(this._dateCreated ?? undefined);
  }

  emptyDetail(): TimeRecordDetail {
    return Timesheet.emptyDetail(this._dateCreated ?? undefined);
  }

  emptyTicketHours(): TimeRecordHours {
    return Timesheet.emptyTicketHours(this._dateCreated ?? undefined);
  }

  emptyDays() {
    return JSON.parse(JSON.stringify(days));
  }

  //empty data (static versions)
  static emptyTimesheet(date = new Date()): TimeRecordHeader {
    return {
      id: undefined,
      userId: undefined,
      user: undefined,
      periodEndingDate: undefined,
      managerApprovedBy: undefined,
      managerApprovedDate: undefined,
      accountingApprovedBy: undefined,
      accountingApprovedDate: undefined,
      lastExportedDate: undefined,
      details: [this.emptyDetail(date)],
      lastUpdatedBy: undefined,
      lastUpdatedDate: undefined,
      createdBy: undefined,
      createdDate: dateFormat(date, "yyyy-mm-dd'T'HH:MM:ss"),
    };
  }

  static emptyDetail(date = new Date()): TimeRecordDetail {
    return {
      id: undefined,
      headerId: undefined,
      jobNumber: undefined,
      description: undefined,
      skill: undefined,
      earningsType: undefined,
      costCode: undefined,
      shopJobPartsID: undefined,
      shopJobParts: undefined,
      header: undefined,
      hours: [],
      costCodes: [],
      createdBy: undefined,
      isPayable: true,
      createdDate: dateFormat(date, "yyyy-mm-dd'T'HH:MM:ss"),
      lastUpdatedBy: undefined,
      lastUpdatedDate: undefined,
    };
  }

  static emptyTicketHours(date = new Date()): TimeRecordHours {
    return {
      id: undefined,
      detailId: undefined,
      dateOfRecord: undefined,
      hours: 0,
      createdDate: dateFormat(date, "yyyy-mm-dd'T'HH:MM:ss"),
      createdBy: undefined,
      lastUpdatedBy: undefined,
      lastUpdatedDate: undefined,
    };
  }

  static emptyDays() {
    return JSON.parse(JSON.stringify(days));
  }
}
